import { memo, useState, useEffect, useRef, useLayoutEffect } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { Section } from '@volt/shared';
import { useI18n } from '../i18n/context.js';
import { useUI } from '../state/ui.js';
import { useConversations } from '../hooks/useConversations.js';
import { useFolders } from '../hooks/useFolders.js';
import { useDebouncedValue } from '../hooks/useDebouncedValue.js';
import { useInfiniteScrollSentinel } from '../hooks/useInfiniteScrollSentinel.js';
import { getRecentConversations } from '../lib/sidebarInteractions.js';
import { ConversationList } from './ConversationList.js';
import { FolderList } from './FolderList.js';
import {
  DropBanner,
  PinnedDropArea,
  SidebarDndProvider,
  UnpinDropArea,
  useSidebarDnd,
} from './SidebarDnd.js';
import { SettingsScreen } from './SettingsScreen.js';
import { Icon } from './Icon.js';
import { getTelegramUser, showBackButton } from '../telegram/webapp.js';
import { hapticFeedback } from '../lib/haptics.js';
import { useAnalyticsTracking } from '../state/analytics.js';

/**
 * A sub-view (folders, archive, all chats) that a swipe to the right takes back to the
 * menu, like the back arrow. The pane follows the finger and slides away on release.
 */
export function SwipeBackPane({ onBack, children }: { onBack: () => void; children: ReactNode }) {
  const dnd = useSidebarDnd();
  const paneRef = useRef<HTMLDivElement>(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const trackingRef = useRef(false);
  const timerRef = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  const settle = (el: HTMLElement, x: string, after?: () => void) => {
    el.style.transition = 'transform 0.24s var(--ease-spring)';
    el.style.transform = `translateX(${x})`;
    timerRef.current = window.setTimeout(() => {
      if (x === '0px') {
        el.style.transition = '';
        el.style.transform = '';
      }
      after?.();
    }, 220);
  };

  return (
    <div
      ref={paneRef}
      className="sidebar-pane sidebar-pane-sub"
      onTouchStart={(event) => {
        trackingRef.current = false;
        startRef.current = null;
        const touch = event.touches[0];
        // A text field keeps its own gestures (selection, caret).
        if (event.touches.length !== 1 || !touch) return;
        if ((event.target as HTMLElement).closest('input, textarea')) return;
        window.clearTimeout(timerRef.current);
        startRef.current = { x: touch.clientX, y: touch.clientY };
      }}
      onTouchMove={(event) => {
        const start = startRef.current;
        const touch = event.touches[0];
        const el = paneRef.current;
        if (!start || !touch || !el) return;
        // A held / dragged row owns the finger.
        if (dnd.isHeld()) {
          startRef.current = null;
          if (trackingRef.current) settle(el, '0px');
          trackingRef.current = false;
          return;
        }
        const dx = touch.clientX - start.x;
        const dy = touch.clientY - start.y;
        if (!trackingRef.current) {
          if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) {
            startRef.current = null; // vertical scroll
            return;
          }
          if (!(dx > 14 && dx > Math.abs(dy) * 1.5)) return;
          trackingRef.current = true;
          el.style.transition = 'none';
        }
        el.style.transform = `translateX(${Math.max(0, dx)}px)`;
      }}
      onTouchEnd={(event) => {
        const start = startRef.current;
        const el = paneRef.current;
        startRef.current = null;
        if (!trackingRef.current || !start || !el) return;
        trackingRef.current = false;
        const dx = (event.changedTouches[0]?.clientX ?? start.x) - start.x;
        if (dx > Math.min(96, window.innerWidth * 0.28)) {
          hapticFeedback('tap');
          settle(el, '100%', onBack);
        } else {
          settle(el, '0px');
        }
      }}
      onTouchCancel={() => {
        startRef.current = null;
        if (trackingRef.current && paneRef.current) settle(paneRef.current, '0px');
        trackingRef.current = false;
      }}
    >
      {children}
    </div>
  );
}

export interface SidebarProps {
  section: Section;
}

type SidebarView = 'menu' | 'chats' | 'folders' | 'archive' | 'all';

const CHAT_HISTORY_LIMIT = 10;

/**
 * Slide-in drawer containing section-based navigation. Memoised so the per-frame App
 * re-renders of a page swipe do not re-render the whole drawer.
 */
export const Sidebar = memo(function Sidebar({ section }: SidebarProps) {
  const { t } = useI18n();
  const {
    drawerOpen,
    closeDrawer,
    activeConversationId,
    selectConversation,
    newChat,
    conversationsVersion,
    refreshConversations,
    foldersVersion,
    refreshFolders,
  } = useUI();
  const [view, setView] = useState<SidebarView>('menu');
  const [search, setSearch] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const analytics = useAnalyticsTracking();
  const searchRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const scrollPosRef = useRef<Partial<Record<SidebarView, number>>>({});
  const viewRef = useRef<SidebarView>('menu');
  viewRef.current = view;
  const debouncedSearch = useDebouncedValue(search, 300);

  const tgUser = getTelegramUser();

  // Load all conversations including archived so we can filter by pane
  const { conversations, cacheReason, hasMore, loadingMore, loadMore } = useConversations(
    section,
    // In the "all" view the server searches titles AND message text.
    view === 'all' ? debouncedSearch.trim() : '',
    true, // include archived
    conversationsVersion,
  );

  // Reload folders and chats from the server each time the drawer opens: another
  // device may have changed them, and a reply in the open chat moves it up the list.
  const prevDrawerOpenRef = useRef(false);
  useEffect(() => {
    if (drawerOpen && !prevDrawerOpenRef.current) {
      refreshFolders();
      refreshConversations();
    }
    prevDrawerOpenRef.current = drawerOpen;
  }, [drawerOpen, refreshFolders, refreshConversations]);

  // "All chats" pages in as the user scrolls towards the end of the list.
  const loadMoreSentinelRef = useInfiniteScrollSentinel<HTMLDivElement>(loadMore, {
    enabled: drawerOpen && view === 'all' && hasMore && !loadingMore,
    rootMargin: '400px',
  });

  const { folders, setFolders } = useFolders(foldersVersion);

  // Save scroll position when the sub-view body is about to unmount (drawer closes or view changes)
  const saveScroll = () => {
    if (viewRef.current !== 'menu' && bodyRef.current) {
      scrollPosRef.current[viewRef.current] = bodyRef.current.scrollTop;
    }
  };

  useEffect(() => {
    if (!drawerOpen) saveScroll();
  }, [drawerOpen]);

  // Restore scroll position synchronously before paint when a sub-view mounts
  useLayoutEffect(() => {
    if (!bodyRef.current || view === 'menu') return;
    bodyRef.current.scrollTop = scrollPosRef.current[view] ?? 0;
  }, [view]);

  // Focus search when switching to "all" view
  useEffect(() => {
    if (view === 'all') {
      window.setTimeout(() => searchRef.current?.focus({ preventScroll: true }), 320);
    }
  }, [view]);

  useEffect(() => {
    if (view === 'all' && debouncedSearch.trim()) analytics.trackSearch();
  }, [analytics, debouncedSearch, view]);

  const navigate = (next: SidebarView) => {
    saveScroll();
    setView(next);
    if (next !== 'all') setSearch('');
  };

  const handleSelectConversation = (id: string) => {
    saveScroll();
    selectConversation(id);
    closeDrawer();
  };

  const goBack = () => {
    saveScroll();
    setView('menu');
  };

  // One step back: a sub-view returns to the menu, the menu closes the drawer.
  // Driven by Escape and by Telegram's native BackButton while the drawer is open.
  const backRef = useRef<() => void>(() => undefined);
  backRef.current = () => {
    if (viewRef.current !== 'menu') goBack();
    else closeDrawer();
  };
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      // A context menu / dialog on top handles its own Escape.
      if (document.querySelector('.menu-overlay, .overlay')) return;
      event.preventDefault();
      backRef.current();
    };
    document.addEventListener('keydown', onKey);
    const hideBackButton = showBackButton(() => backRef.current());
    return () => {
      document.removeEventListener('keydown', onKey);
      hideBackButton();
    };
  }, [drawerOpen]);

  // ── Derived lists ──────────────────────────────────────────────
  const regularConvs = conversations.filter((c) => !c.isArchived);
  const unfolderedConvs = regularConvs.filter((c) => c.folderId == null && !c.isPinned);
  const archivedConvs = conversations.filter((c) => c.isArchived);
  const pinnedConvs = conversations.filter((c) => c.isPinned && !c.isArchived);
  const recentConvs = getRecentConversations(conversations, CHAT_HISTORY_LIMIT);

  // Search is applied by the server (titles + message content); offline the
  // cached list is filtered by title in useConversations.
  const allConvs = conversations.filter((c) => !c.isArchived);

  const NAV_ITEMS: {
    key: SidebarView;
    icon: Parameters<typeof Icon>[0]['name'];
    label: string;
    count?: number;
  }[] = [
    { key: 'folders', icon: 'folder', label: t('sidebar.folders'), count: folders.length },
    { key: 'archive', icon: 'archive', label: t('sidebar.archived'), count: archivedConvs.length },
  ];

  const isSubView = view !== 'menu';
  const subViewLabel =
    view === 'all'
      ? t('sidebar.allChats')
      : (NAV_ITEMS.find((item) => item.key === view)?.label ?? '');

  return (
    <SidebarDndProvider section={section}>
      <aside
        className={`sidebar${drawerOpen ? ' is-open' : ''}`}
        aria-label={t('sidebar.title')}
        aria-hidden={!drawerOpen}
      >
        {/* Header */}
        <div className={`sidebar-head${isSubView ? ' is-subview' : ''}`}>
          {isSubView ? (
            <span className="sidebar-head-spacer" aria-hidden="true" />
          ) : (
            <button
              type="button"
              className="sidebar-close-btn icon-btn"
              onClick={closeDrawer}
              aria-label={t('common.close')}
            >
              <Icon name="close" size={20} />
            </button>
          )}
          <span className="sidebar-title">{isSubView ? subViewLabel : t('sidebar.title')}</span>
          {isSubView ? (
            <button
              type="button"
              className="sidebar-back-btn icon-btn"
              onClick={goBack}
              aria-label={t('common.back')}
            >
              <Icon name="back" size={20} />
            </button>
          ) : null}
        </div>

        {cacheReason === 'offline' && (
          <p className="sidebar-cache-hint">{t('common.offline')}</p>
        )}

        {/* Sliding content pane */}
        <div className="sidebar-pane-host">
          {/* Root menu */}
          <div
            className={`sidebar-pane sidebar-pane-menu${isSubView ? ' is-exit' : ' is-enter'}`}
          >
            <nav className="sidebar-nav" aria-label={t('sidebar.title')}>
              {NAV_ITEMS.map(({ key, icon, label, count }) => (
                <button
                  key={key}
                  type="button"
                  className="sidebar-nav-item"
                  onClick={() => navigate(key)}
                >
                  <span className="sidebar-nav-icon">
                    <Icon name={icon} size={20} />
                  </span>
                  <span className="sidebar-nav-label">{label}</span>
                  {count !== undefined && count > 0 && (
                    <span className="sidebar-nav-count">{count}</span>
                  )}
                  <Icon name="chevronRight" size={16} className="sidebar-nav-arrow" />
                </button>
              ))}
            </nav>
            <div className="sidebar-menu-recent">
              {/* While a chat is dragged: the pinned group is the drop target, or a
                  floating "drop to pin" banner when nothing is pinned yet. */}
              {pinnedConvs.length > 0 ? (
                <PinnedDropArea>
                  <ConversationList
                    conversations={pinnedConvs}
                    folders={folders}
                    section={section}
                    activeId={activeConversationId}
                    onSelect={handleSelectConversation}
                  />
                </PinnedDropArea>
              ) : (
                <DropBanner mode="pin" />
              )}
              <UnpinDropArea>
                {recentConvs.length > 0 && (
                  <ConversationList
                    conversations={recentConvs}
                    folders={folders}
                    section={section}
                    activeId={activeConversationId}
                    onSelect={handleSelectConversation}
                  />
                )}
              </UnpinDropArea>
              <button
                type="button"
                className="sidebar-all-chats-btn"
                onClick={() => navigate('all')}
              >
                <span className="sidebar-nav-icon">
                  <Icon name="more" size={20} />
                </span>
                <span className="sidebar-nav-label">{t('sidebar.allChats')}</span>
                <Icon name="chevronRight" size={16} className="sidebar-nav-arrow" />
              </button>
            </div>
          </div>

          {/* Sub-view (mounted only when a sub-view is active) */}
          {isSubView && (
            <SwipeBackPane onBack={goBack}>
              {view === 'all' && (
                <div className="sidebar-search-top">
                  <div className="sidebar-search">
                    <Icon name="search" size={16} className="sidebar-search-icon" />
                    <input
                      ref={searchRef}
                      type="search"
                      className="sidebar-search-input"
                      placeholder={t('sidebar.searchPlaceholder')}
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      aria-label={t('sidebar.search')}
                    />
                    {search && (
                      <button
                        type="button"
                        className="icon-btn sidebar-search-clear"
                        aria-label={t('common.close')}
                        onClick={() => setSearch('')}
                      >
                        <Icon name="close" size={16} />
                      </button>
                    )}
                  </div>
                </div>
              )}
              <div className="sidebar-body" ref={bodyRef}>
                {view !== 'archive' && (
                  <>
                    <DropBanner mode="pin" />
                    <DropBanner mode="unpin" />
                  </>
                )}
                {view === 'chats' && (
                  <ConversationList
                    conversations={unfolderedConvs}
                    folders={folders}
                    section={section}
                    activeId={activeConversationId}
                    onSelect={handleSelectConversation}
                  />
                )}
                {view === 'folders' && (
                  <FolderList
                    folders={folders}
                    conversations={regularConvs}
                    section={section}
                    activeId={activeConversationId}
                    onSelectConversation={handleSelectConversation}
                    onFoldersChanged={(updater) => {
                      setFolders((prev) =>
                        typeof updater === 'function' ? updater(prev) : updater,
                      );
                    }}
                  />
                )}
                {view === 'archive' && (
                  <ConversationList
                    conversations={archivedConvs}
                    folders={folders}
                    section={section}
                    activeId={activeConversationId}
                    onSelect={handleSelectConversation}
                  />
                )}
                {view === 'all' && (
                  <>
                    <ConversationList
                      conversations={allConvs}
                      folders={folders}
                      section={section}
                      activeId={activeConversationId}
                      onSelect={handleSelectConversation}
                      flatList
                    />
                    {hasMore && (
                      <div
                        ref={loadMoreSentinelRef}
                        className="sidebar-load-more"
                        aria-hidden={!loadingMore}
                        role={loadingMore ? 'status' : undefined}
                      >
                        {loadingMore && <span className="spinner" />}
                      </div>
                    )}
                  </>
                )}
              </div>
            </SwipeBackPane>
          )}
        </div>

        {/* Bottom bar */}
        <div className="sidebar-foot sidebar-foot-bar">
          <button
            type="button"
            className="btn btn-primary sidebar-new-btn"
            onClick={newChat}
            aria-label={t('sidebar.newChat')}
          >
            <Icon name="plus" size={18} />
            <span>{t('sidebar.newChat')}</span>
          </button>

          <button
            type="button"
            className="sidebar-profile-btn"
            onClick={() => setSettingsOpen(true)}
            aria-label={t('topbar.settings')}
          >
            {tgUser?.photo_url ? (
              <img
                src={tgUser.photo_url}
                alt=""
                className="sidebar-profile-avatar"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <span className="sidebar-profile-avatar-placeholder">
                <Icon name="user" size={16} />
              </span>
            )}
          </button>
        </div>

      </aside>

      {createPortal(
        <SettingsScreen
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
        />,
        document.body,
      )}
    </SidebarDndProvider>
  );
});
